import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Pencil, Plus, Trash2, PackageSearch } from "lucide-react";
import TrackingDialog from "../components/TrackingDialog";

const EMPTY = {
  brand: "",
  category: "lips",
  name_en: "",
  name_ar: "",
  desc_en: "",
  desc_ar: "",
  price: 0,
  image: "",
  featured: false,
  active: true,
  offer: null,
};

export default function Admin() {
  const { t } = useLang();
  const { user } = useAuth();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [trackingOrder, setTrackingOrder] = useState(null);
  const [showCredit, setShowCredit] = useState(false);
  const [creditTarget, setCreditTarget] = useState(null);
  const [creditAmount, setCreditAmount] = useState("");
  const [creditNote, setCreditNote] = useState("");

  const loadProducts = () =>
    api.get("/products", { params: { include_inactive: true } }).then(({ data }) => setProducts(data));
  const loadUsers = () => api.get("/admin/users").then(({ data }) => setUsers(data));
  const loadOrders = () => api.get("/admin/orders").then(({ data }) => setOrders(data));
  const loadStats = () => api.get("/admin/stats").then(({ data }) => setStats(data));

  useEffect(() => {
    if (user?.role !== "admin") return;
    loadProducts();
    loadStats();
  }, [user]);

  useEffect(() => {
    if (tab === "users") loadUsers();
    if (tab === "orders") loadOrders();
  }, [tab]);

  if (user?.role !== "admin") {
    return <div className="p-16 text-center text-[#767676]">Admin only</div>;
  }

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      brand: p.brand, category: p.category,
      name_en: p.name_en, name_ar: p.name_ar,
      desc_en: p.desc_en, desc_ar: p.desc_ar,
      price: p.price, image: p.image,
      featured: p.featured, active: p.active,
      offer: p.offer,
    });
    setShowForm(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/admin/products/${editing.id}`, form);
        toast.success("Updated");
      } else {
        await api.post("/admin/products", form);
        toast.success("Created");
      }
      setShowForm(false);
      loadProducts();
      loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted");
    loadProducts();
    loadStats();
  };

  const toggleActive = async (p) => {
    await api.patch(`/admin/products/${p.id}`, { active: !p.active });
    loadProducts();
  };
  const toggleFeatured = async (p) => {
    await api.patch(`/admin/products/${p.id}`, { featured: !p.featured });
    loadProducts();
  };

  const creditUser = (u) => {
    setCreditTarget(u);
    setCreditAmount("");
    setCreditNote("POS top-up");
    setShowCredit(true);
  };

  const submitCredit = async () => {
    const amount = parseFloat(creditAmount);
    if (!Number.isFinite(amount) || amount === 0) {
      toast.error("Enter a non-zero amount");
      return;
    }
    try {
      const { data } = await api.post("/admin/wallet/credit", {
        user_id: creditTarget.id,
        amount,
        note: creditNote || `Manual by admin`,
      });
      toast.success(`${creditTarget.email} → $${data.new_balance.toFixed(2)}`);
      setShowCredit(false);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const toggleRole = async (u) => {
    const nextRole = u.role === "admin" ? "user" : "admin";
    const label = nextRole === "admin" ? "promote to admin" : "remove admin role";
    if (!window.confirm(`${label.charAt(0).toUpperCase() + label.slice(1)} for ${u.email}?`)) return;
    try {
      await api.patch(`/admin/users/${u.id}/role`, { role: nextRole });
      toast.success(`${u.email} → ${nextRole}`);
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const setOrderStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}/status`, { status });
      toast.success(`Status → ${status}`);
      loadOrders();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const openTracking = async (id) => {
    try {
      const { data } = await api.get(`/admin/orders/${id}`);
      setTrackingOrder(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <main data-testid="admin-page" className="max-w-7xl mx-auto px-6 md:px-10 py-10">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#7B5CBF]">{t("admin")}</div>
          <h1 className="font-display text-4xl mt-2">{t("dashboard")}</h1>
        </div>
        <div className="hidden md:flex gap-8 text-sm">
          <Stat label={t("products")} value={stats.products || 0} />
          <Stat label={t("users")} value={stats.users || 0} />
          <Stat label={t("ordersAdmin")} value={stats.orders || 0} />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="admin-tabs" className="bg-[#F3F0EB]">
          <TabsTrigger value="products" data-testid="tab-products">{t("products")}</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">{t("users")}</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">{t("ordersAdmin")}</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button onClick={openNew} data-testid="add-product-btn" className="rounded-full bg-[#1A1738] text-white">
              <Plus className="h-4 w-4 me-2" strokeWidth={1.5} /> {t("add")}
            </Button>
          </div>
          <div className="border border-[#E5E1D8] rounded-md overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-[#F3F0EB] text-[#767676] uppercase text-[11px] tracking-widest">
                <tr>
                  <th className="text-start p-3">Product</th>
                  <th className="text-start p-3 hidden md:table-cell">Brand</th>
                  <th className="text-start p-3 hidden md:table-cell">Category</th>
                  <th className="text-start p-3">Price</th>
                  <th className="text-start p-3">Featured</th>
                  <th className="text-start p-3">Active</th>
                  <th className="text-end p-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E1D8]">
                {products.map((p) => (
                  <tr key={p.id} data-testid={`row-product-${p.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <img src={p.image} alt="" className="h-10 w-10 object-cover rounded" />
                        <div className="max-w-xs">
                          <div className="font-medium truncate">{p.name_en}</div>
                          <div className="text-xs text-[#767676] truncate">{p.name_ar}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 hidden md:table-cell">{p.brand}</td>
                    <td className="p-3 hidden md:table-cell capitalize">{p.category}</td>
                    <td className="p-3">
                      ${p.price.toFixed(2)}
                      {p.offer > 0 && <span className="ms-2 text-xs text-[#7B5CBF]">-{p.offer}%</span>}
                    </td>
                    <td className="p-3">
                      <Switch
                        checked={p.featured}
                        onCheckedChange={() => toggleFeatured(p)}
                        data-testid={`toggle-featured-${p.id}`}
                      />
                    </td>
                    <td className="p-3">
                      <Switch
                        checked={p.active}
                        onCheckedChange={() => toggleActive(p)}
                        data-testid={`toggle-active-${p.id}`}
                      />
                    </td>
                    <td className="p-3 text-end">
                      <button onClick={() => openEdit(p)} data-testid={`edit-${p.id}`} className="p-2 hover:bg-[#F3F0EB] rounded">
                        <Pencil className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                      <button onClick={() => del(p.id)} data-testid={`del-${p.id}`} className="p-2 hover:bg-[#F3F0EB] rounded text-[#7B5CBF]">
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <div className="border border-[#E5E1D8] rounded-md overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-[#F3F0EB] text-[#767676] uppercase text-[11px] tracking-widest">
                <tr>
                  <th className="text-start p-3">Phone</th>
                  <th className="text-start p-3">Name</th>
                  <th className="text-start p-3">Role</th>
                  <th className="text-start p-3">Wallet</th>
                  <th className="text-end p-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E1D8]">
                {users.map((u) => (
                  <tr key={u.id} data-testid={`row-user-${u.id}`}>
                    <td className="p-3 font-mono text-xs">{u.phone || "—"}<div className="text-[10px] text-[#767676] font-sans">{u.email}</div></td>
                    <td className="p-3">{u.name}</td>
                    <td className="p-3 capitalize">{u.role}</td>
                    <td className="p-3 font-medium">${u.wallet_balance.toFixed(2)}</td>
                    <td className="p-3 text-end">
                      {u.id !== user.id && (
                        <button
                          onClick={() => toggleRole(u)}
                          data-testid={`role-${u.id}`}
                          className="me-2 px-3 h-8 rounded-full border border-[#E5E1D8] text-xs hover:bg-[#F3F0EB]"
                        >
                          {u.role === "admin" ? "Remove admin" : "Make admin"}
                        </button>
                      )}
                      {u.role !== "admin" && (
                        <Button
                          size="sm"
                          onClick={() => creditUser(u)}
                          data-testid={`credit-${u.id}`}
                          className="rounded-full bg-[#1A1738] text-white h-8"
                        >
                          {t("creditWallet")}
                        </Button>
                      )}                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          <div className="border border-[#E5E1D8] rounded-md overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-[#F3F0EB] text-[#767676] uppercase text-[11px] tracking-widest">
                <tr>
                  <th className="text-start p-3">Order</th>
                  <th className="text-start p-3">Customer</th>
                  <th className="text-start p-3">Items</th>
                  <th className="text-start p-3">Total</th>
                  <th className="text-start p-3">Status</th>
                  <th className="text-start p-3">Date</th>
                  <th className="text-end p-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E1D8]">
                {orders.map((o) => (
                  <tr key={o.id} data-testid={`row-order-${o.id}`}>
                    <td className="p-3 font-mono text-xs">{o.order_number}</td>
                    <td className="p-3">
                      <div className="text-sm">{o.shipping_name || "—"}</div>
                      <div className="text-xs text-[#767676]">{o.user_email}</div>
                    </td>
                    <td className="p-3">{o.items_count}</td>
                    <td className="p-3">${o.total.toFixed(2)}</td>
                    <td className="p-3">
                      <select
                        value={o.status}
                        onChange={(e) => setOrderStatus(o.id, e.target.value)}
                        data-testid={`status-${o.id}`}
                        className="border border-[#E5E1D8] rounded px-2 py-1 text-xs bg-white capitalize"
                      >
                        {["paid","processing","shipped","delivered","cancelled"].map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-3 text-xs text-[#767676]">{new Date(o.created_at).toLocaleString()}</td>
                    <td className="p-3 text-end">
                      <button
                        onClick={() => openTracking(o.id)}
                        data-testid={`track-${o.id}`}
                        className="inline-flex items-center gap-1 px-3 h-8 rounded-full border border-[#E5E1D8] text-xs hover:bg-[#F3F0EB]"
                      >
                        <PackageSearch className="h-3.5 w-3.5" strokeWidth={1.5} /> Track
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Product form dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing ? t("edit") : t("add")} — {t("products")}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <FormField label={t("brandLabel")}>
              <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="form-brand" />
            </FormField>
            <FormField label={t("categoryLabel")}>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="form-category"
                className="h-10 w-full border border-[#E5E1D8] rounded-md px-3 bg-white"
              >
                <option value="lips">lips</option>
                <option value="eyes">eyes</option>
                <option value="face">face</option>
                <option value="accessories">accessories</option>
              </select>
            </FormField>
            <FormField label="Name (EN)">
              <Input value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} data-testid="form-name-en" />
            </FormField>
            <FormField label="Name (AR)">
              <Input value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} data-testid="form-name-ar" />
            </FormField>
            <FormField label="Desc (EN)" full>
              <Textarea value={form.desc_en} onChange={(e) => setForm({ ...form, desc_en: e.target.value })} data-testid="form-desc-en" rows={3} />
            </FormField>
            <FormField label="Desc (AR)" full>
              <Textarea value={form.desc_ar} onChange={(e) => setForm({ ...form, desc_ar: e.target.value })} data-testid="form-desc-ar" rows={3} dir="rtl" />
            </FormField>
            <FormField label={t("price") + " (USD)"}>
              <Input type="number" step="0.01" value={form.price}
                onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
                data-testid="form-price" />
            </FormField>
            <FormField label={t("offerPct")}>
              <Input type="number" step="1" min="0" max="90" value={form.offer ?? ""}
                onChange={(e) => setForm({ ...form, offer: e.target.value === "" ? null : parseFloat(e.target.value) })}
                data-testid="form-offer" />
            </FormField>
            <FormField label={t("image")} full>
              <Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="form-image" />
            </FormField>
            <FormField label={t("featured")}>
              <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="form-featured" />
            </FormField>
            <FormField label={t("active")}>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="form-active" />
            </FormField>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} data-testid="form-cancel">{t("cancel")}</Button>
            <Button onClick={save} data-testid="form-save" className="bg-[#1A1738] text-white">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {trackingOrder && (
        <TrackingDialog
          order={trackingOrder}
          onClose={() => setTrackingOrder(null)}
          onChanged={(updated) => { setTrackingOrder(updated); loadOrders(); }}
        />
      )}

      {/* Credit wallet dialog */}
      <Dialog open={showCredit} onOpenChange={setShowCredit}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{t("creditWallet")}</DialogTitle>
          </DialogHeader>
          {creditTarget && (
            <div className="space-y-4">
              <div className="text-sm text-[#767676]">
                <span className="font-mono">{creditTarget.phone || creditTarget.email}</span>
                <span className="ms-2">
                  ({t("balance")}: <span className="font-medium text-[#1A1738]">${creditTarget.wallet_balance.toFixed(2)}</span>)
                </span>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#767676]">
                  {t("amount")} (USD)
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  placeholder="e.g. 50 (or -10 to debit)"
                  data-testid="credit-amount-input"
                  className="mt-1 h-11 bg-white"
                  autoFocus
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#767676]">{t("note")}</Label>
                <Input
                  value={creditNote}
                  onChange={(e) => setCreditNote(e.target.value)}
                  placeholder="POS reference…"
                  data-testid="credit-note-input"
                  className="mt-1 h-11 bg-white"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowCredit(false)}
                  data-testid="credit-cancel-btn"
                >
                  {t("cancel")}
                </Button>
                <Button
                  onClick={submitCredit}
                  data-testid="credit-submit-btn"
                  className="bg-[#1A1738] hover:bg-[#2C2650] text-white"
                >
                  {t("save")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#767676]">{label}</div>
      <div className="font-display text-2xl">{value}</div>
    </div>
  );
}

function FormField({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-xs uppercase tracking-widest text-[#767676]">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
